import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { fetchMarketingConsent, fetchReferral, setMarketingConsent, type ReferralInfo } from "@/lib/api";
import { haptic, openExternalLink } from "@/lib/telegram";

/** Profile: news/offers opt-in switch. Order and "back in stock" messages are not affected. */
/** `variant="row"` renders it as a row inside a profile group (same logic). */
export function MarketingToggle({ variant = "card", icon }: { variant?: "card" | "row"; icon?: React.ReactNode } = {}) {
  const { t } = useI18n();
  const [on, setOn] = useState<boolean | null>(null);

  useEffect(() => {
    fetchMarketingConsent().then(setOn).catch(() => setOn(null));
  }, []);

  if (on === null) return null;
  const toggle = async () => {
    haptic("selection");
    const next = !on;
    setOn(next);
    try {
      setOn(await setMarketingConsent(next));
    } catch {
      setOn(!next);
    }
  };

  if (variant === "row") {
    return (
      <button className="pf-row" onClick={toggle} role="switch" aria-checked={on}>
        <span className="pf-ic">{icon}</span>
        <span className="pf-row__label">{t("marketing.toggleTitle")}</span>
        <span className="switch" data-on={on} aria-hidden="true" style={{ marginLeft: "auto" }} />
      </button>
    );
  }
  return (
    <button className="card switch-row" onClick={toggle} role="switch" aria-checked={on}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14.5 }}>{t("marketing.toggleTitle")}</span>
        <span style={{ display: "block", fontSize: 12, color: "var(--color-ink-soft)", marginTop: 3 }}>{t("marketing.toggleHint")}</span>
      </span>
      <span className="switch" data-on={on} aria-hidden="true" />
    </button>
  );
}

/** Profile: "Invite a friend" card (only when the referral program is switched on). */
export function ReferralCard() {
  const { t } = useI18n();
  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferral().then(setInfo).catch(() => setInfo(null));
  }, []);
  if (!info?.link) return null;

  const share = () => {
    haptic("light");
    const url = `https://t.me/share/url?url=${encodeURIComponent(info.link!)}&text=${encodeURIComponent(t("referral.shareText"))}`;
    openExternalLink(url);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.link!);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <section className="brand-panel" style={{ marginTop: 0, marginBottom: 12 }}>
      <span className="eyebrow">{t("referral.eyebrow")}</span>
      <h3>{t("referral.title")}</h3>
      <p>{t("referral.body")}</p>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button className="btn btn--primary" style={{ flex: 1 }} onClick={share}>
          {t("referral.share")}
        </button>
        <button className="btn btn--ghost" onClick={copy}>
          {copied ? t("referral.copied") : t("referral.copy")}
        </button>
      </div>
      {info.invited > 0 && <p style={{ marginTop: 10, fontSize: 12.5 }}>{t("referral.stats", { invited: info.invited, qualified: info.qualified })}</p>}
    </section>
  );
}
